/**
 * @file SdekPickupPointPicker.tsx
 * @description Sheet-picker для выбора ПВЗ СДЭК (phase10.5 шаг 5).
 *
 * Открывается с правой стороны (desktop) / снизу (mobile). Поиск по городу
 * через debounced-input. Список ПВЗ — из cache `/admin/carriers/sdek/pickup-points`.
 *
 * При пустом cache backend возвращает mock-данные (5 ПВЗ Москва/СПб/Челябинск),
 * чтобы dev-окружение работало без реальной интеграции.
 */

import { Loader2, MapPin, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { ApiError } from "../../lib/api-client";
import {
  listSdekPickupPoints,
  type PickupPointDto,
} from "../../lib/api-carriers-sdek";

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (point: PickupPointDto) => void;
}

export function SdekPickupPointPicker({ open, onClose, onPick }: Props) {
  const [city, setCity] = useState("");
  const [items, setItems] = useState<PickupPointDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listSdekPickupPoints({ city: city.trim() || undefined });
        if (!cancelled) setItems(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, city]);

  const grouped = useMemo(() => {
    if (!items) return null;
    const map = new Map<string, PickupPointDto[]>();
    for (const p of items) {
      const list = map.get(p.city) ?? [];
      list.push(p);
      map.set(p.city, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Выбрать ПВЗ СДЭК</SheetTitle>
          <SheetDescription>
            Введите название города, чтобы найти ближайший пункт выдачи.
          </SheetDescription>
        </SheetHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-4 text-on-surface-variant" />
          <Input
            placeholder="Город (например, Москва)"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="size-6 animate-spin text-on-surface-variant" />
            </div>
          ) : error ? (
            <p className="text-error text-sm py-4">{error}</p>
          ) : !grouped || grouped.length === 0 ? (
            <p className="text-on-surface-variant text-sm py-8 text-center">
              ПВЗ не найдены. Попробуйте другой город.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {grouped.map(([cityName, points]) => (
                <div key={cityName} className="flex flex-col gap-2">
                  <h3 className="text-xs uppercase tracking-wide text-on-surface-variant font-semibold">
                    {cityName}
                  </h3>
                  {points.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        onPick(p);
                        onClose();
                      }}
                      className="text-left p-3 rounded-md border border-outline-variant hover:bg-surface-low transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="size-4 mt-0.5 text-on-surface-variant shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{p.address}</p>
                          {p.workingHours ? (
                            <p className="text-xs text-on-surface-variant mt-0.5">
                              {p.workingHours}
                            </p>
                          ) : null}
                          {/* Телефон пункта простым текстом: вся карточка — кнопка
                              выбора, вкладывать в неё tel:-ссылку нельзя. */}
                          {p.phone ? (
                            <p className="text-xs text-on-surface-variant mt-0.5">{p.phone}</p>
                          ) : null}
                          <p className="text-[10px] uppercase tracking-wide text-on-surface-variant mt-1">
                            {p.externalCode}
                            {p.hasCashPay ? " · оплата на месте" : ""}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
